import { RuleSetType, RuleType } from '@fruitful/types';

import { IRA_ANNUAL_LIMIT_CENTS } from './builders';
import type { MoneyMapSimulation } from './types';

export interface PresetOption {
    label: string;
    description: string;
    moneyMap: MoneyMapSimulation;
}

const BALANCED_SAVER: MoneyMapSimulation = {
    monthlyGoalsCents: 800000,
    incomeAccountId: 'checking-main',
    ruleSets: [
        {
            id: 'rs-emergency',
            type: RuleSetType.EmergencyFund,
            name: 'Emergency Fund',
            priority: 1,
            rules: [
                {
                    id: 'rule-emergency',
                    type: RuleType.FullEmergencyFund,
                    description: 'Build emergency fund to $20k',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountId: 'savings-emergency',
                    allocationType: 'MINIMUM_BALANCE',
                    targetBalanceCents: 2000000,
                    monthlyContributionMaxCents: 100000,
                },
            ],
        },
        {
            id: 'rs-debt',
            type: RuleSetType.FinancialHealth,
            name: 'Debt Payoff',
            priority: 2,
            rules: [
                {
                    id: 'rule-debt-avalanche',
                    type: RuleType.DebtPayDown,
                    description: 'Pay off debts (avalanche)',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountIds: ['debt-credit-card', 'debt-student-loan'],
                    allocationType: 'DEBT_PAY_DOWN',
                    strategy: 'AVALANCHE',
                },
            ],
        },
        {
            id: 'rs-invest',
            type: RuleSetType.FinancialHealth,
            name: 'Investments',
            priority: 3,
            rules: [
                {
                    id: 'rule-roth',
                    type: RuleType.Retirement,
                    description: 'Max Roth IRA',
                    priority: 1,
                    splitPercentage: 0.5,
                    accountId: 'invest-roth',
                    allocationType: 'PERCENTAGE',
                    percentage: 1.0,
                    yearlyContributionMaxCents: IRA_ANNUAL_LIMIT_CENTS,
                },
                {
                    id: 'rule-brokerage',
                    type: RuleType.Savings,
                    description: 'Remaining to brokerage',
                    priority: 2,
                    splitPercentage: 0.5,
                    accountId: 'invest-brokerage',
                    allocationType: 'PERCENTAGE',
                    percentage: 1.0,
                },
            ],
        },
    ],
    accounts: [
        {
            id: 'checking-main',
            name: 'Main Checking',
            type: 'CHECKING',
            balanceCents: 500000,
        },
        {
            id: 'savings-emergency',
            name: 'Emergency Fund',
            type: 'SAVINGS',
            balanceCents: 1200000,
        },
        {
            id: 'debt-student-loan',
            name: 'Student Loan',
            type: 'DEBT',
            balanceCents: 2500000,
            annualInterestRate: 0.055,
            minimumPaymentCents: 28000,
        },
        {
            id: 'debt-credit-card',
            name: 'Credit Card',
            type: 'DEBT',
            balanceCents: 800000,
            annualInterestRate: 0.219,
            minimumPaymentCents: 25000,
        },
        {
            id: 'invest-roth',
            name: 'Roth IRA',
            type: 'ROTH_IRA',
            balanceCents: 4500000,
            annualContributionLimitCents: IRA_ANNUAL_LIMIT_CENTS,
            initialYtdContributionCents: 350000,
        },
        {
            id: 'invest-brokerage',
            name: 'Brokerage',
            type: 'TAXABLE_INVESTMENT',
            balanceCents: 1500000,
        },
    ],
};

const AGGRESSIVE_DEBT_PAYOFF: MoneyMapSimulation = {
    monthlyGoalsCents: 650000,
    incomeAccountId: 'checking',
    ruleSets: [
        {
            id: 'rs-mini-efund',
            type: RuleSetType.EmergencyFund,
            name: 'Mini Emergency Fund',
            priority: 1,
            rules: [
                {
                    id: 'rule-mini-efund',
                    type: RuleType.StarterEmergencyFund,
                    description: 'Starter $1k emergency fund',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountId: 'savings-starter',
                    allocationType: 'MINIMUM_BALANCE',
                    targetBalanceCents: 100000,
                },
            ],
        },
        {
            id: 'rs-debt',
            type: RuleSetType.FinancialHealth,
            name: 'Debt Avalanche',
            priority: 2,
            rules: [
                {
                    id: 'rule-debt-avalanche',
                    type: RuleType.DebtPayDown,
                    description: 'Pay off all debts (avalanche)',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountIds: ['debt-cc-high', 'debt-cc-low', 'debt-car'],
                    allocationType: 'DEBT_PAY_DOWN',
                    strategy: 'AVALANCHE',
                },
            ],
        },
        {
            id: 'rs-savings',
            type: RuleSetType.EmergencyFund,
            name: 'Full Emergency Fund',
            priority: 3,
            rules: [
                {
                    id: 'rule-full-efund',
                    type: RuleType.FullEmergencyFund,
                    description: 'Build to $15k emergency fund',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountId: 'savings-starter',
                    allocationType: 'MINIMUM_BALANCE',
                    targetBalanceCents: 1500000,
                    monthlyContributionMaxCents: 50000,
                },
            ],
        },
    ],
    accounts: [
        {
            id: 'checking',
            name: 'Checking',
            type: 'CHECKING',
            balanceCents: 300000,
        },
        {
            id: 'savings-starter',
            name: 'Emergency Fund',
            type: 'SAVINGS',
            balanceCents: 50000,
        },
        {
            id: 'debt-cc-high',
            name: 'Visa Platinum (24.9%)',
            type: 'DEBT',
            balanceCents: 1200000,
            annualInterestRate: 0.249,
            minimumPaymentCents: 35000,
        },
        {
            id: 'debt-cc-low',
            name: 'Chase Freedom (18.9%)',
            type: 'DEBT',
            balanceCents: 450000,
            annualInterestRate: 0.189,
            minimumPaymentCents: 15000,
        },
        {
            id: 'debt-car',
            name: 'Auto Loan (6.5%)',
            type: 'DEBT',
            balanceCents: 1800000,
            annualInterestRate: 0.065,
            minimumPaymentCents: 35000,
        },
    ],
};

const HIGH_EARNER_INVEST: MoneyMapSimulation = {
    monthlyGoalsCents: 1500000,
    incomeAccountId: 'checking',
    ruleSets: [
        {
            id: 'rs-safety',
            type: RuleSetType.EmergencyFund,
            name: 'Safety Net',
            priority: 1,
            rules: [
                {
                    id: 'rule-efund',
                    type: RuleType.FullEmergencyFund,
                    description: 'Maintain $50k emergency fund',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountId: 'savings-efund',
                    allocationType: 'MINIMUM_BALANCE',
                    targetBalanceCents: 5000000,
                },
            ],
        },
        {
            id: 'rs-retirement',
            type: RuleSetType.FinancialHealth,
            name: 'Retirement',
            priority: 2,
            rules: [
                {
                    id: 'rule-roth',
                    type: RuleType.Retirement,
                    description: 'Max Roth IRA',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountId: 'roth-ira',
                    allocationType: 'PERCENTAGE',
                    percentage: 1.0,
                    yearlyContributionMaxCents: IRA_ANNUAL_LIMIT_CENTS,
                },
            ],
        },
        {
            id: 'rs-goals',
            type: RuleSetType.FinancialHealth,
            name: 'Goals',
            priority: 3,
            rules: [
                {
                    id: 'rule-house',
                    type: RuleType.Savings,
                    description: 'House down payment ($2k/mo)',
                    priority: 1,
                    splitPercentage: 0.5,
                    accountId: 'savings-house',
                    allocationType: 'PERCENTAGE',
                    percentage: 1.0,
                    monthlyContributionMaxCents: 200000,
                },
                {
                    id: 'rule-vacation',
                    type: RuleType.Savings,
                    description: 'Travel fund ($500/mo)',
                    priority: 2,
                    splitPercentage: 0.5,
                    accountId: 'savings-vacation',
                    allocationType: 'PERCENTAGE',
                    percentage: 1.0,
                    monthlyContributionMaxCents: 50000,
                },
            ],
        },
        {
            id: 'rs-invest',
            type: RuleSetType.FinancialHealth,
            name: 'Taxable Investing',
            priority: 4,
            rules: [
                {
                    id: 'rule-brokerage',
                    type: RuleType.Savings,
                    description: 'Everything else to brokerage',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountId: 'brokerage',
                    allocationType: 'PERCENTAGE',
                    percentage: 1.0,
                },
            ],
        },
    ],
    accounts: [
        {
            id: 'checking',
            name: 'Primary Checking',
            type: 'CHECKING',
            balanceCents: 1000000,
        },
        {
            id: 'savings-efund',
            name: 'Emergency Fund',
            type: 'SAVINGS',
            balanceCents: 4500000,
        },
        {
            id: 'savings-house',
            name: 'House Fund',
            type: 'SAVINGS',
            balanceCents: 2500000,
        },
        {
            id: 'savings-vacation',
            name: 'Travel Fund',
            type: 'SAVINGS',
            balanceCents: 150000,
        },
        {
            id: 'roth-ira',
            name: 'Roth IRA',
            type: 'ROTH_IRA',
            balanceCents: 8500000,
            annualContributionLimitCents: IRA_ANNUAL_LIMIT_CENTS,
            initialYtdContributionCents: 0,
        },
        {
            id: 'brokerage',
            name: 'Brokerage',
            type: 'TAXABLE_INVESTMENT',
            balanceCents: 5000000,
        },
    ],
};

const SIMPLE_STARTER: MoneyMapSimulation = {
    monthlyGoalsCents: 450000,
    incomeAccountId: 'checking',
    ruleSets: [
        {
            id: 'rs-save',
            type: RuleSetType.EmergencyFund,
            name: 'Savings',
            priority: 1,
            rules: [
                {
                    id: 'rule-efund',
                    type: RuleType.FullEmergencyFund,
                    description: 'Emergency fund ($500/mo cap)',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountId: 'savings',
                    allocationType: 'MINIMUM_BALANCE',
                    targetBalanceCents: 1000000,
                    monthlyContributionMaxCents: 50000,
                },
            ],
        },
        {
            id: 'rs-debt',
            type: RuleSetType.FinancialHealth,
            name: 'Debt',
            priority: 2,
            rules: [
                {
                    id: 'rule-cc',
                    type: RuleType.DebtPayDown,
                    description: 'Pay off credit card',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountIds: ['debt-cc'],
                    allocationType: 'DEBT_PAY_DOWN',
                    strategy: 'AVALANCHE',
                },
            ],
        },
        {
            id: 'rs-invest',
            type: RuleSetType.FinancialHealth,
            name: 'Investing',
            priority: 3,
            rules: [
                {
                    id: 'rule-roth',
                    type: RuleType.Retirement,
                    description: 'Start Roth IRA',
                    priority: 1,
                    splitPercentage: 1.0,
                    accountId: 'roth',
                    allocationType: 'PERCENTAGE',
                    percentage: 1.0,
                    yearlyContributionMaxCents: IRA_ANNUAL_LIMIT_CENTS,
                },
            ],
        },
    ],
    accounts: [
        {
            id: 'checking',
            name: 'Checking',
            type: 'CHECKING',
            balanceCents: 200000,
        },
        {
            id: 'savings',
            name: 'Savings',
            type: 'SAVINGS',
            balanceCents: 300000,
        },
        {
            id: 'debt-cc',
            name: 'Credit Card (22%)',
            type: 'DEBT',
            balanceCents: 350000,
            annualInterestRate: 0.22,
            minimumPaymentCents: 10000,
        },
        {
            id: 'roth',
            name: 'Roth IRA',
            type: 'ROTH_IRA',
            balanceCents: 0,
            annualContributionLimitCents: IRA_ANNUAL_LIMIT_CENTS,
            initialYtdContributionCents: 0,
        },
    ],
};

export const PRESETS: PresetOption[] = [
    {
        label: 'Balanced Saver',
        description:
            '$8k/mo income, emergency fund + debt payoff + Roth IRA + brokerage',
        moneyMap: BALANCED_SAVER,
    },
    {
        label: 'Aggressive Debt Payoff',
        description: '$6.5k/mo income, debt avalanche strategy across 3 debts',
        moneyMap: AGGRESSIVE_DEBT_PAYOFF,
    },
    {
        label: 'High Earner Investor',
        description:
            '$15k/mo income, max IRAs, house fund, travel, then brokerage',
        moneyMap: HIGH_EARNER_INVEST,
    },
    {
        label: 'Simple Starter',
        description:
            '$4.5k/mo income, build emergency fund, pay off one CC, start Roth',
        moneyMap: SIMPLE_STARTER,
    },
];
